import React from 'react';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import { withPathParamProvider } from '@helpcenter/with-path-params';
import type { Route, RouteContext } from 'react-resource-router';
import { NotFoundPage, RenderCreateAnonymousSuccess, RenderRequestCreateForHubs } from 'view/app/routes';
import { CsmArticle } from 'view/article/csm-variants';
import { CompleteSignup } from 'view/complete-signup';
import { Login } from 'view/login';
import { PortalAccessForbidden } from 'view/portal-access-forbidden';
import { ResetPassword } from 'view/reset-password';
import { SignUp } from 'view/sign-up';
import { CheckEmail } from 'view/sign-up-check-email';
import FeatureGates from '@atlaskit/feature-gate-js-client';
// eslint-disable-next-line no-restricted-imports
import { HelpHelpCenterAri } from '@atlassian/ari';
import type { HubAppProps } from '@atlassian/company-hub-common';
import { CsmContactUsForHubs } from '@atlassian/csm-contact-us';
import { CsmFolderForHubs } from '@atlassian/csm-folder';
import { CsmEmptyFolderResource } from '@atlassian/csm-folder/src/services/csm-empty-folder-resource';
import { CsmFolderResource } from '@atlassian/csm-folder/src/services/csm-folder-resource';
import { csmRequestDetailsQueryResource } from '@atlassian/csm-request-details/src/services/cms-request-details-resource';
import { mediaReadCredentialsResource } from '@atlassian/csm-request-details/src/services/media-read-credentials-resource';
import { mediaUploadCredentialsResource } from '@atlassian/csm-request-details/src/services/media-upload-credentials-resource';
import { requestConversationResource } from '@atlassian/csm-request-details/src/services/request-conversation-resource';
import { AsyncCsmRequestDetails } from '@atlassian/csm-request-details/src/ui/index';
import { CsmRequestsListForHubs } from '@atlassian/csm-requests-list';
import { csmRequestsListQueryResource } from '@atlassian/csm-requests-list/src/services/csm-requests-list-resource';
import { mediaUploadCredentialsResource as mediaUploadCredentialsResourceForContactUs } from '@atlassian/customer-service/src/services/media-credentials-upload-resource';
import { getIsHelpHub } from '@atlassian/help-center-common-util/advanced-help-center';
import { getHoistedProjectKey, getJiraDisplayUrl } from '@atlassian/help-center-common-util/env';
import { getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import { initialModel } from '@atlassian/help-center-common-util/model';
import { withSetThemeColorInRedux } from '@atlassian/help-center-common-util/with-set-theme-color-in-redux';
import { Search } from '@atlassian/help-center-search';
import {
    createHubHomepageRoute,
    createRouteWithHubShell as createRouteWithHubShellWithoutWrapper,
    createHubCustomPageRoute,
    createHubPortalPageRoute,
} from '@atlassian/hub';
import { logOutAction } from '../../epics/logout';
import { store } from '../../providers';
import { ApprovalList } from '../approval-list';
import { Article } from '../article';
import { HubAnonymousLayout } from '../hub-anonymous-layout';
import { HubAvatarMenuOptions } from '../hub-avatar-menu-options';
import { RequestApproval } from '../request-approval';
import { RequestDetails } from '../request-details';
import { RequestFeedback } from '../request-feedback';
import { RequestList } from '../request-list';
import { Unsubscribe } from '../unsubscribe';
import { CsmUserProfileForHubs } from '../user-profile/csm-hub-variants';
import { UserProfileForHubs } from '../user-profile/hub-variant';
import { HUB_ROUTES } from './constants';

const helpCenterAri = getHelpCenterAri();

const renderAvatarMenuOptions = () => {
    return <HubAvatarMenuOptions />;
};

const createHubRouteProps = (shouldRenderRightNavActions: boolean): HubAppProps => {
    const initialModelState = initialModel();

    const renderRightNavActions = () => {
        return <Search trigger="icon" />;
    };

    const isHelpHub = getIsHelpHub();

    return {
        renderRightNavActions: shouldRenderRightNavActions ? renderRightNavActions : undefined,
        renderAvatarMenuOptions: isHelpHub ? renderAvatarMenuOptions : undefined,
        user: {
            email: initialModelState.user?.email || '',
            displayName: initialModelState.user?.displayName || '',
            avatarSrc: initialModelState.user?.avatars?.xlarge || '',
            isJiraAdmin: initialModelState.user?.canAdministerJIRA || false,
            id: '',
        },
        env: {
            helpCenterAri,
            cloudId: getCloudId(),
            helpCenterId:
                helpCenterAri && helpCenterAri !== 'null' ? HelpHelpCenterAri.parse(helpCenterAri).helpCenterId : '',
            jiraDisplayUrl: getJiraDisplayUrl(),
            hoistedProjectKey: getHoistedProjectKey() || '',
            relativeBaseUrl: getRelativeBasePath(),
        },
        actions: {
            onLogoutClick: () => {
                const action = logOutAction();
                store.dispatch(action);
                return action;
            },
        },
    };
};

const createRouteWithHubShell = (...args: Parameters<typeof createRouteWithHubShellWithoutWrapper>) => {
    const [props, component, ...restArgs] = args;
    return createRouteWithHubShellWithoutWrapper(
        props,
        withSetThemeColorInRedux(withPathParamProvider(component)),
        ...restArgs
    );
};

export const getHubHomepageRoute = (): Route => createHubHomepageRoute(createHubRouteProps(false));

export const getHubCustompageRoute = (): Route => createHubCustomPageRoute(createHubRouteProps(true));

export const getHubPortalPageRoute = (): Route => createHubPortalPageRoute(createHubRouteProps(true));

export const getCsmUserProfileRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        CsmUserProfileForHubs,
        HUB_ROUTES.USER_PROFILE.path,
        HUB_ROUTES.USER_PROFILE.name,
        [],
        'full-width'
    );
export const getCsmRequestsListRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        CsmRequestsListForHubs,
        HUB_ROUTES.CSM_REQUESTS_LIST.path,
        HUB_ROUTES.CSM_REQUESTS_LIST.name,
        [csmRequestsListQueryResource],
        'full-width'
    );

export const getCsmContactUsRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        CsmContactUsForHubs,
        HUB_ROUTES.CSM_CONTACT_US.path,
        HUB_ROUTES.CSM_CONTACT_US.name,
        [mediaUploadCredentialsResourceForContactUs],
        'full-width'
    );

export const getNotFoundRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(true), layout: { showBanner: true, bannerText: '', isAnonymousRoute: false } },
        NotFoundPage,
        HUB_ROUTES.NOT_FOUND.path,
        HUB_ROUTES.NOT_FOUND.name,
        [],
        'normal'
    );

export const getCsmFolderRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        CsmFolderForHubs,
        HUB_ROUTES.CSM_FOLDER.path,
        HUB_ROUTES.CSM_FOLDER.name,
        [CsmFolderResource, CsmEmptyFolderResource],
        'full-width'
    );

export const getCsmRequestDetailsRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        AsyncCsmRequestDetails,
        HUB_ROUTES.CSM_REQUEST_DETAILS.path,
        HUB_ROUTES.CSM_REQUEST_DETAILS.name,
        [
            csmRequestDetailsQueryResource,
            requestConversationResource,
            mediaReadCredentialsResource,
            mediaUploadCredentialsResource,
        ],
        'xxwide'
    );

const articlePageWidth = FeatureGates.checkGate('fix_kb_article_scroll') ? 'wide' : 'normal';

export const getCsmViewPortalArticleRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        CsmArticle,
        HUB_ROUTES.VIEW_PORTAL_ARTICLE.path,
        HUB_ROUTES.VIEW_PORTAL_ARTICLE.name,
        [],
        articlePageWidth
    );

export const getHubViewPortalArticleRoute = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        Article,
        HUB_ROUTES.VIEW_PORTAL_ARTICLE.path,
        HUB_ROUTES.VIEW_PORTAL_ARTICLE.name,
        [],
        articlePageWidth
    );

export const getPortalAccessForbiddenRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        PortalAccessForbidden,
        HUB_ROUTES.PORTAL_ACCESS_FORBIDDEN.path,
        HUB_ROUTES.PORTAL_ACCESS_FORBIDDEN.name,
        [],
        'normal'
    );

export const getCsmViewArticleRouteWithHubShell = (): Route => {
    if (FeatureGates.checkGate('csm_support_site_article_view_custom_width_fix')) {
        return createRouteWithHubShell(
            createHubRouteProps(true),
            CsmArticle,
            HUB_ROUTES.VIEW_ARTICLE.path,
            HUB_ROUTES.VIEW_ARTICLE.name,
            [],
            'full-width'
        );
    }

    return createRouteWithHubShell(
        createHubRouteProps(true),
        CsmArticle,
        HUB_ROUTES.VIEW_ARTICLE.path,
        HUB_ROUTES.VIEW_ARTICLE.name,
        [],
        'normal'
    );
};

export const getHubViewArticleRoute = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        Article,
        HUB_ROUTES.VIEW_ARTICLE.path,
        HUB_ROUTES.VIEW_ARTICLE.name,
        [],
        'normal'
    );

export const getHubViewKbRoute = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(true), appearance: 'full-width' },
        Article,
        HUB_ROUTES.VIEW_KB_ARTICLE.path,
        HUB_ROUTES.VIEW_KB_ARTICLE.name,
        [],
        'normal'
    );

export const getCsmViewCrossSitePortalArticleRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        CsmArticle,
        HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.path,
        HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.name,
        [],
        'normal'
    );
export const getHubViewCrossSitePortalArticleRoute = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        Article,
        HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.path,
        HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.name,
        [],
        'normal'
    );

export const getHubViewCrossSiteArticleRoute = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        Article,
        HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.path,
        HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.name,
        [],
        'normal'
    );

export const getCsmViewCrossSiteArticleRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        CsmArticle,
        HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.path,
        HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.name,
        [],
        'normal'
    );

export const getLoginRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(false), layout: { showBanner: true, bannerText: '', isAnonymousRoute: true } },
        () => (
            <HubAnonymousLayout>
                <Login />
            </HubAnonymousLayout>
        ),
        HUB_ROUTES.LOGIN.path,
        HUB_ROUTES.LOGIN.name,
        [],
        'note'
    );

export const getSignupRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(false), layout: { showBanner: true, bannerText: '', isAnonymousRoute: true } },
        (routeProps: Omit<RouteContext, 'action'>) => (
            <HubAnonymousLayout>
                <SignUp {...routeProps} />
            </HubAnonymousLayout>
        ),
        HUB_ROUTES.SIGNUP.path,
        HUB_ROUTES.SIGNUP.name,
        [],
        'note'
    );

export const getCompleteSignupRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(false), layout: { showBanner: true, bannerText: '', isAnonymousRoute: true } },
        (routeProps: Omit<RouteContext, 'action'>) => (
            <HubAnonymousLayout>
                <CompleteSignup {...routeProps} />
            </HubAnonymousLayout>
        ),
        HUB_ROUTES.COMPLETE_SIGNUP.path,
        HUB_ROUTES.COMPLETE_SIGNUP.name,
        [],
        'note'
    );

export const getResetPasswordRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(false), layout: { showBanner: true, bannerText: '', isAnonymousRoute: true } },
        (routeProps: Omit<RouteContext, 'action'>) => (
            <HubAnonymousLayout>
                <ResetPassword {...routeProps} />
            </HubAnonymousLayout>
        ),
        HUB_ROUTES.RESET_PASSWORD.path,
        HUB_ROUTES.RESET_PASSWORD.name,
        [],
        'note'
    );

export const getCheckEmailRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(false), layout: { showBanner: true, bannerText: '', isAnonymousRoute: true } },
        (routeProps: Omit<RouteContext, 'action'>) => (
            <HubAnonymousLayout>
                <CheckEmail {...routeProps} isLoading={false} />
            </HubAnonymousLayout>
        ),
        HUB_ROUTES.CHECK_EMAIL.path,
        HUB_ROUTES.CHECK_EMAIL.name,
        [],
        'note'
    );

export const getUnsubscribeRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(true), layout: { showBanner: true, bannerText: '', isAnonymousRoute: false } },
        Unsubscribe,
        HUB_ROUTES.UNSUBSCRIBE.path,
        HUB_ROUTES.UNSUBSCRIBE.name,
        [],
        'note'
    );

export const getRequestFeedbackRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(true), layout: { showBanner: true, bannerText: '', isAnonymousRoute: false } },
        RequestFeedback,
        HUB_ROUTES.REQUEST_FEEDBACK.path,
        HUB_ROUTES.REQUEST_FEEDBACK.name,
        [],
        'note'
    );

export const getAnonymousRequestSuccessRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(true), layout: { showBanner: true, bannerText: '', isAnonymousRoute: false } },
        RenderCreateAnonymousSuccess,
        HUB_ROUTES.ANONYMOUS_REQUEST_SUCCESS.path,
        HUB_ROUTES.ANONYMOUS_REQUEST_SUCCESS.name,
        [],
        'xxwide'
    );

export const getRequestCreateRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        RenderRequestCreateForHubs,
        HUB_ROUTES.REQUEST_CREATE.path,
        HUB_ROUTES.REQUEST_CREATE.name,
        [],
        'full-width'
    );

export const getHubUserProfileRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        UserProfileForHubs,
        HUB_ROUTES.USER_PROFILE.path,
        HUB_ROUTES.USER_PROFILE.name,
        [],
        'full-width'
    );

export const getJsmRequestDetailsRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        createHubRouteProps(true),
        RequestDetails,
        HUB_ROUTES.JSM_REQUEST_DETAILS.path,
        HUB_ROUTES.JSM_REQUEST_DETAILS.name,
        [],
        'xxwide'
    );

export const getJsmRequestsListRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        /**
         * TODO: The banner text has to be internationalized
         */
        { ...createHubRouteProps(true), layout: { showBanner: true, bannerText: 'Requests', isAnonymousRoute: false } },
        RequestList,
        HUB_ROUTES.JSM_REQUESTS_LIST.path,
        HUB_ROUTES.JSM_REQUESTS_LIST.name,
        [],
        'xxwide'
    );

export const getJsmApprovalsRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        {
            ...createHubRouteProps(true),
            layout: { showBanner: true, bannerText: 'Approvals', isAnonymousRoute: false },
        },
        ApprovalList,
        HUB_ROUTES.JSM_APPROVALS.path,
        HUB_ROUTES.JSM_APPROVALS.name,
        [],
        'xxwide'
    );

export const getJsmRequestApprovalRouteWithHubShell = (): Route =>
    createRouteWithHubShell(
        { ...createHubRouteProps(true), layout: { showBanner: true, bannerText: '', isAnonymousRoute: false } },
        RequestApproval,
        HUB_ROUTES.JSM_REQUEST_APPROVAL.path,
        HUB_ROUTES.JSM_REQUEST_APPROVAL.name,
        [],
        'xxwide'
    );
